import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Animated, Modal, ScrollView, TextStyle, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import CustomButton, { ButtonTypes } from '../../CustomButton';
import { translate } from '../../../services/translate';
import { faBarsSort, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import MyAppText from '../../MyAppText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface FilterSectionProps {
    children: React.ReactNode;
    sectionTitle?: string;
    sectionTitleStyle?: TextStyle;
}

export function FilterSection({ children, sectionTitle, sectionTitleStyle }: FilterSectionProps) {
    const { styles } = useStyles(stylesheet);

    return (
        <>
            <MyAppText style={[styles.sectionTitle, sectionTitleStyle]}>
                {sectionTitle}
            </MyAppText>
            {children}
        </>
    );
}


interface Props {
    children: React.ReactNode;
    onCleanFilters: () => void;
    onFilters: () => void;
    onStatusChange?: (isOpen: boolean) => void;
    filterIsLoading?: boolean;
    applyIsLoading?: boolean;
    applyDisabled?: boolean;
    cleanDisabled?: boolean;
    applyText?: string;
    cancelText?: string;
    type?: ButtonTypes;
    filterCount?: number;
    btnContent?: React.ReactNode;
    headerTitle?: string;
    subheaderTitle?: string;
    primaryButton?: boolean;
}

export interface FiltersRef {
    open: () => void;
    close: () => void;
    toggle: () => void;
}

/**
 * @deprecated
 * use ModernFilters/FilterModal instead
 */
const Filters = forwardRef<FiltersRef, Props>(function Filters(
    { children, onCleanFilters, onFilters, onStatusChange, applyIsLoading, applyDisabled, cleanDisabled, filterCount, btnContent, headerTitle, subheaderTitle, applyText, cancelText, primaryButton }: Props,
    ref
) {
    const { styles, breakpoint, theme } = useStyles(stylesheet);
    const { width, height } = useWindowDimensions();

    const sidebarWidth = useMemo(() => {
        if (breakpoint === 'xs') {
            return width;
        }

        return 460;
    }, [width, breakpoint]);

    const [isOpen, setIsOpen] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);

    useImperativeHandle(ref, () => ({
        open: () => setIsOpen(true),
        close: () => setIsOpen(false),
        toggle: () => setIsOpen(prev => !prev),
    }), []);

    const animationSidebar = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        Animated.timing(animationSidebar, {
            toValue: isOpen ? sidebarWidth : 0,
            duration: 200,
            useNativeDriver: true
        }).start();

        onStatusChange?.(isOpen);
    }, [isOpen, sidebarWidth]);

    useEffect(() => {
        if (isOpen) {
            setIsModalVisible(true);
        } else {
            setTimeout(() => setIsModalVisible(false), 200);
        }
    }, [isOpen]);

    return <>
        <TouchableOpacity style={primaryButton ? styles.filterButtonPrimary : styles.filterButton} onPress={() => setIsOpen(!isOpen)}>
            <View style={styles.filterButtonContent}>
                {btnContent || <>
                    <FontAwesomeIcon
                        icon={faBarsSort as IconDefinition}
                        color={theme.colors.secondaryButton.text}
                        fontSize={16}
                    />
                    <MyAppText style={primaryButton ? styles.filterButtonTextPrimary : styles.filterButtonText}>{translate('filters')}</MyAppText>
                    {!!filterCount && filterCount > 0 && (
                        <View style={styles.badge}>
                            <MyAppText style={styles.badgeText}>{filterCount}</MyAppText>
                        </View>
                    )}
                </>}
            </View>
        </TouchableOpacity>
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => setIsOpen(false)}
        >
            <View
                style={{
                    backgroundColor: '#31313199',
                    flex: 1,
                }}
            >
                <Animated.View
                    style={[
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        { position: 'fixed' },
                        styles.container(height),
                        { width: animationSidebar, borderWidth: isOpen ? 1 : 0 },
                    ]}
                >
                    {isOpen &&
                        <>
                            <View style={styles.header}>
                                <View style={styles.headerTopRow}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                                        <MyAppText style={{ fontSize: 18, fontWeight: '600' }}>{headerTitle || translate('allFilters')}</MyAppText>
                                        {!!filterCount && filterCount > 0 && (
                                            <View style={styles.badge}>
                                                <MyAppText style={styles.badgeText}>{filterCount}</MyAppText>
                                            </View>
                                        )}
                                    </View>

                                    <TouchableOpacity onPress={() => setIsOpen(!isOpen)}>
                                        <FontAwesomeIcon
                                            icon={faXmark as IconDefinition}
                                            color={theme.colors.iconColor}
                                            fontSize={24}
                                        />
                                    </TouchableOpacity>
                                </View>
                                <MyAppText style={styles.headerSubtitle}>
                                    {subheaderTitle || translate('selectFiltersToApply')}
                                </MyAppText>
                            </View>

                            <View style={[styles.filterContainer]}>
                                <View style={styles.inputsHeight}>
                                    <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent} style={{ scrollbarGutter: 'stable' } as object}>
                                        <View style={styles.children}>
                                            {children}
                                        </View>
                                    </ScrollView>
                                </View>
                                <View style={styles.buttonsContainer}>
                                    <CustomButton
                                        contentContainerStyle={{ flex: 1 }}
                                        text={cancelText || translate('cleanFilters')}
                                        onPress={onCleanFilters}
                                        type='secondary'
                                        disabled={cleanDisabled}
                                    />
                                    <CustomButton
                                        contentContainerStyle={{ flex: 1 }}
                                        style={{ paddingHorizontal: 0 }}
                                        text={applyText || translate('filterResults')}
                                        onPress={() => {
                                            onFilters();
                                            setIsOpen(!isOpen);
                                        }}
                                        disabled={applyDisabled}
                                        isLoading={applyIsLoading}
                                    />
                                </View>
                            </View>
                        </>
                    }
                </Animated.View>
            </View>
        </Modal>
    </>;
});

const stylesheet = createStyleSheet(theme => ({
    container: (height: number) => {
        return {
            backgroundColor: theme.colors.container.background,
            top: 0,
            height: height,
            right: 0,
            zIndex: 10,
            margin: 0,
            borderWidth: 0,
            borderColor: 'transparent',
            padding: 32,
            gap: 40,
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
        };
    },

    header: {
        flexDirection: 'column',
        gap: 4,
    },
    headerTopRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    headerSubtitle: {
        fontSize: 16,
        color: theme.colors.neutral['400'],
        fontWeight: '600',
    },

    filterContainer: {
        flex: 1,
        gap: 40
    },

    inputsHeight: {
        flex: 1,
    },

    buttonsContainer: {
        flexDirection: 'row',
        gap: 24,
    },

    children: {
        zIndex: 2,
        gap: 16
    },
    scrollContent: {
        flex: 1,
        gap: 20,
        justifyContent: 'space-between',
    },
    filterButtonPrimary: {
        borderWidth: 0,
        borderColor: undefined,
        backgroundColor: theme.colors.primaryButton.background,
        borderRadius: 5,
        paddingHorizontal: 20,
        height: 40,
        justifyContent: 'center',
    },
    filterButton: {
        borderWidth: 1,
        borderColor: theme.colors.secondaryButton.borderColor,
        backgroundColor: theme.colors.secondaryButton.background,
        borderRadius: 5,
        paddingHorizontal: 20,
        height: 40,
        justifyContent: 'center',
    },
    filterButtonContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    filterButtonTextPrimary: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.primaryButton.text,
    },
    filterButtonText: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.secondaryButton.text,
    },
    badge: {
        backgroundColor: theme.colors.primary['500'],
        borderRadius: 4,
        paddingHorizontal: 6,
        paddingVertical: 2,
        minWidth: 22,
        alignItems: 'center',
        justifyContent: 'center',
    },
    badgeText: {
        color: theme.colors.white['500'],
        fontSize: 14,
        fontWeight: 'bold',
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.primary['500'],
        paddingTop: 16,
        textTransform: 'uppercase',
    },
}));

export default Filters;
